import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1658247298686-36f43d1dd08e?crop=entropy&cs=srgb&fm=jpg&q=85";

// Same logo set as the main site MINUS Spotify Editorial, BBC World Service, and TIME
const LOGOS = [
  {
    name: "Billboard",
    src: "https://customer-assets.emergentagent.com/job_producer-vilas-beats/artifacts/2rvr4mzy_Billboard_logo_PNG_%2810%29.png",
  },
  {
    name: "Rolling Stone",
    src: "https://customer-assets.emergentagent.com/job_producer-vilas-beats/artifacts/u9xo6eoq_Rolling-Stone-Logo.jpg",
  },
  {
    name: "HBO",
    src: "https://customer-assets.emergentagent.com/job_producer-vilas-beats/artifacts/pxbzuwtm_hbo-logo-png_seeklogo-65764.png",
  },
  {
    name: "Netflix",
    src: "https://customer-assets.emergentagent.com/job_producer-vilas-beats/artifacts/mdo35r63_Netflix_2015_logo.svg.png",
  },
];

function setSeoMeta() {
  document.title = "Paul Vilas | Your Private Producer";
  const setMeta = (name, content, attr = "name") => {
    let tag = document.querySelector(`meta[${attr}="${name}"]`);
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute(attr, name);
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
  };
  setMeta(
    "description",
    "One-on-one music production, songwriting and release support for independent artists. Finish your songs and bring them to life with Paul Vilas."
  );
  setMeta("og:title", "Paul Vilas | Your Private Producer", "property");
  setMeta(
    "og:description",
    "One-on-one music production, songwriting and release support for independent artists.",
    "property"
  );
  setMeta("og:url", "https://paulvilas.com/start", "property");
}

export default function Start() {
  const navigate = useNavigate();

  useEffect(() => {
    setSeoMeta();
  }, []);

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-black text-white flex flex-col"
      style={{
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Warm cinematic overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(8,6,3,0.78) 0%, rgba(15,11,5,0.72) 50%, rgba(8,6,3,0.88) 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-30"
        style={{
          background:
            "radial-gradient(circle at 50% 40%, rgba(245,193,75,0.45), transparent 60%)",
        }}
      />

      {/* Brand mark */}
      <header className="relative z-10 w-full flex justify-center pt-8 md:pt-10">
        <BrandLogo variant="yellow" className="h-9 md:h-11" />
      </header>

      {/* Hero — centered */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl mx-auto text-center flex flex-col items-center">
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold leading-[0.95] tracking-tight">
            Your
            <br />
            Private
            <br />
            <span className="text-paul-yellow">Studio</span>
          </h1>

          <p className="mt-8 md:mt-10 text-lg md:text-xl text-white max-w-xl leading-relaxed font-medium">
            Just you and me finishing your songs and getting them release-ready.
          </p>

          <Button
            onClick={() => navigate("/inquiry")}
            className="mt-10 bg-paul-yellow hover:bg-paul-yellow text-navy font-bold text-base md:text-lg px-12 md:px-16 py-7 md:py-8 rounded-md shadow-[0_10px_40px_rgba(245,193,75,0.35)] hover:shadow-[0_14px_50px_rgba(245,193,75,0.5)] transition-shadow group"
            style={{ color: "#0b1535" }}
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
          </Button>

          {/* Social proof */}
          <div className="mt-14 md:mt-16 w-full flex flex-col items-center">
            <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-paul-yellow/90 font-semibold mb-5">
              Artists Featured On
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {LOGOS.map((logo) => (
                <div
                  key={logo.name}
                  className="bg-white/95 rounded-md flex items-center justify-center shadow-sm h-12 w-28 md:h-14 md:w-32 px-3"
                >
                  <img
                    src={logo.src}
                    alt={logo.name}
                    title={logo.name}
                    className="max-h-7 md:max-h-8 max-w-full w-auto object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Trust line */}
          <p className="mt-12 text-sm md:text-base text-white/65 italic max-w-lg">
            Helping artists finish songs, release music, and build momentum.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 w-full text-center pb-8">
        <p className="text-[11px] text-white/40 tracking-widest uppercase">
          © {new Date().getFullYear()} Paul Vilas. Private one-on-one
          production.
        </p>
      </footer>
    </div>
  );
}
